import { useState, useReducer, useRef } from 'react';

import { fetchPostRepaymentRegisterRepaymentMethod } from '@iwoca/lapi-client/edge';
import { Button, Label } from '@iwoca/orion';
import { Form, Formik } from 'formik';
import { Frames, CardNumber, ExpiryDate, Cvv, CardType } from 'frames-react';

import styles from './AddPaymentMethodForm.module.css';
import { Auth3DSecureModal } from './Auth3DSecureModal/Auth3DSecureModal';
import { CheckoutFormValues } from './CheckoutAddPaymentMethodForm.types';
import { pollCardLinking } from './pollCardLinking';
import { validateCheckoutAddCardForm } from './validateFormFields';
import { useGetFundingRequirement } from '../../../../../api/lending/lapiHooks';
import { Input } from '../../../../../components/Input/Input';
import { InputError } from '../../../../../components/InputError/InputError';
import { useStateKey } from '../../../../../hooks/useStateKey.hook';
import { getRuntimeEnvironment } from '../../../../../utils/getRuntimeEnvironment';
import logger from '../../../../../utils/logger';
import { WhiteLabelOverride } from '../../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useIsNonLinearFlow } from '../../../hooks/useIsNonLinearFlow.hook';
import { useNavigateToBasePath } from '../../../hooks/useNavigateToBasePath';
import { useNavigateToNextRequirement } from '../../../hooks/useNavigateToNextRequirement';

const CHECKOUT_COM_PRODUCTION_KEY = 'pk_m6yjih7mvailyyn4fmgunatoiao';
const CHECKOUT_COM_STAGING_KEY = 'pk_sbox_y67mbbuym66rphki3ktkvbjugmt';

export const CheckoutComAddPaymentMethodForm = () => {
  const publicKey =
    getRuntimeEnvironment() === 'production'
      ? CHECKOUT_COM_PRODUCTION_KEY
      : CHECKOUT_COM_STAGING_KEY;

  const { isNonLinearFlow } = useIsNonLinearFlow();
  const goToBasePath = useNavigateToBasePath();

  const { goToNextRequirement } = useNavigateToNextRequirement();
  const forceUpdate = useReducer((x) => x + 1, 0)[1];
  const { fetchFundingRequirements } = useGetFundingRequirement({
    queryOptions: {
      enabled: false,
    },
  });
  const { stateKey } = useStateKey();
  const [submitError, setSubmitError] = useState<string | null>();
  const [postcode, setPostcode] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [auth3DSRedirectUrl, setAuth3DSRedirectUrl] = useState<string | null>();
  const [cardTypeError, setCardTypeError] = useState(false);
  const confirmAuth3DSecureCompleteRef = useRef<() => void>(null);

  const handleAuth3DSecureRequired = (url: string): Promise<void> => {
    return new Promise((resolve) => {
      setAuth3DSRedirectUrl(url);
      confirmAuth3DSecureCompleteRef.current = resolve;
    });
  };

  const handleAuth3DSecureComplete = () => {
    confirmAuth3DSecureCompleteRef.current!();
    setAuth3DSRedirectUrl(null);
  };

  const handleSubmitAddCheckoutCard = async () => {
    setCardTypeError(false);

    if (!stateKey) {
      throw new Error('No statekey provided');
    }

    // 1. Submit card element to Checkout.com
    const { token, card_type } = await Frames.submitCard();

    // 2. Register tokenised card in the backend through an authentication payment
    try {
      const response = await fetchPostRepaymentRegisterRepaymentMethod({
        stateKey: stateKey,
        body: {
          data: {
            token,
            card_type: card_type as Uppercase<CardType>, // Type for card_type is wrong - thanks Checkout.com :(
          },
        },
      });

      const redirect_url = response.data.checkout_3ds_redirect_link;
      if (redirect_url) {
        await handleAuth3DSecureRequired(redirect_url);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setCardTypeError(true);
      Frames.init(); // This resets <Frames /> so a new card can be resubmitted
    }

    // 3. Poll to see if the payment method has been registered
    // poll the card linking as it takes an undetermined amount of time
    // for the Checkout.com webhook to be registered
    const cardLinked = await pollCardLinking(stateKey);
    if (!cardLinked) {
      logger.error('Cant find primary payment card');
      throw new Error(
        'An error occurred while processing your payment method. Please try again.',
      );
    }
  };

  const initialValues: CheckoutFormValues = {
    billingPostcode: '',
    cardholderName: '',
    frames: '',
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => validateCheckoutAddCardForm(values)}
      onSubmit={async (_, { resetForm }) => {
        setSubmitError(null);

        try {
          await handleSubmitAddCheckoutCard();

          await fetchFundingRequirements();
          forceUpdate();

          if (isNonLinearFlow) goToBasePath();
          else goToNextRequirement();
        } catch (error: unknown) {
          const err = error as Error;
          setSubmitError(err.message);
          Frames.init();
          resetForm();
        }
      }}
    >
      {({ errors, touched, isValid, isValidating, dirty, isSubmitting }) => (
        <Form>
          {cardTypeError && (
            <p className="text-error">
              Credit cards are not supported for automatic repayments. Please
              use a debit card.
            </p>
          )}

          <Frames
            config={{
              publicKey: publicKey,
              cardholder: {
                name: cardholderName,
                billingAddress: { zip: postcode },
              },
              localization: {
                cardNumberPlaceholder: '1234 1234 1234 1234',
                expiryMonthPlaceholder: 'MM',
                expiryYearPlaceholder: 'YY',
                cvvPlaceholder: 'CVC',
              },
              style: {
                base: {
                  height: '43px',
                  borderRadius: '10px',
                  border: '1px solid rgba(204, 204, 204, 1)',
                  fontWeight: '400',
                  fontFamily: 'Aesop, sans-serif',
                  fontSize: '14px',
                  padding: '0.5rem',
                  width: 'calc(100% - 2px)',
                },
                focus: {
                  borderColor: 'rgb(105, 159, 227)',
                  borderWidth: '3px',
                },
                invalid: {
                  color: 'rgb(175 13 4)',
                },
                placeholder: {
                  base: {
                    color: '#8e8e8e',
                  },
                },
              },
            }}
          >
            <div className="grid h-[100px] grid-cols-5 gap-l">
              <div className="fs-mask col-span-3">
                <Label className="mb-m">Card number</Label>
                <CardNumber />
              </div>

              <div className="fs-mask">
                <Label className="mb-m">Expiry</Label>
                <ExpiryDate />
              </div>

              <div className="fs-mask">
                <Label className="mb-m">CVC</Label>
                <Cvv />
              </div>
            </div>
          </Frames>

          <div className={styles.billingFields}>
            <div>
              <Input
                className="fs-mask"
                label="Billing postcode"
                name="billingPostcode"
                placeholder="e.g. N6 6NG"
                showError={false}
                onChange={(e) => {
                  setPostcode(e.target.value);
                }}
                type="text"
              />
              <InputError
                isVisible={touched.billingPostcode || false}
                error={errors.billingPostcode}
              />
            </div>
            <div>
              <Input
                className="fs-mask"
                label="Cardholder name"
                name="cardholderName"
                placeholder="John Smith"
                showError={false}
                type="text"
                onChange={(e) => {
                  setCardholderName(e.target.value);
                }}
              />
              <InputError
                isVisible={touched.cardholderName || false}
                error={errors.cardholderName}
              />
            </div>
          </div>

          <WhiteLabelOverride
            componentType="button"
            disabled={isValidating || !(isValid && dirty)}
          >
            <Button
              disabled={isValidating || !(isValid && dirty)}
              loading={isSubmitting}
            >
              Add debit card now
            </Button>
          </WhiteLabelOverride>

          <InputError isVisible={!!errors.frames} error={errors.frames} />
          <InputError
            isVisible={submitError !== undefined}
            error={submitError || ''}
          />

          {auth3DSRedirectUrl && (
            <Auth3DSecureModal
              auth3DSRedirectUrl={auth3DSRedirectUrl}
              onAuth3DSecureComplete={handleAuth3DSecureComplete}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};
